<script setup lang="ts">
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  initialName: string
  /** Resolves to an error message, or undefined once the contact exists. */
  create: (input: { name: string; notes?: string }) => Promise<string | undefined>
}>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const name = ref(props.initialName)
const notes = ref('')
const isSubmitting = ref(false)
const error = ref<string | undefined>(undefined)
const nameInput = useTemplateRef<HTMLInputElement>('nameInput')

const canSubmit = computed(() => name.value.trim().length > 0 && !isSubmitting.value)

onMounted(() => nameInput.value?.focus())

async function submit(): Promise<void> {
  if (!canSubmit.value) return
  isSubmitting.value = true
  error.value = await props.create({
    name: name.value.trim(),
    ...(notes.value.trim() === '' ? {} : { notes: notes.value.trim() }),
  })
  isSubmitting.value = false
}
</script>

<template>
  <Dialog open :label="t('entries.newContactTitle')" @close="emit('close')">
    <form class="flex flex-col gap-3 p-[var(--space-block)]" @submit.prevent="submit">
      <h2 class="text-[length:var(--text-step-4)] text-[color:var(--color-text)]">
        {{ t('entries.newContactTitle') }}
      </h2>
      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('contacts.nameLabel')
        }}</span>
        <input
          ref="nameInput"
          v-model="name"
          type="text"
          maxlength="200"
          required
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('entries.newContactNotes')
        }}</span>
        <textarea
          v-model="notes"
          maxlength="1000"
          rows="3"
          class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
        />
      </label>
      <StatusTag v-if="error !== undefined" tone="danger" role="alert">{{ error }}</StatusTag>
      <div class="flex gap-2">
        <ActionButton type="submit" :disabled="!canSubmit">
          {{ isSubmitting ? t('contacts.saving') : t('contacts.save') }}
        </ActionButton>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
          @click="emit('close')"
        >
          {{ t('contacts.cancel') }}
        </button>
      </div>
    </form>
  </Dialog>
</template>
